import {useCallback, useEffect, useState} from 'react';

const LOG_PREFIX = '[useEntityNotes]';
const BASE = (process.env.REACT_APP_AWP_API_BASE || process.env.REACT_APP_AWP_API_URL || '')
    .replace(/\/$/, '');

function authHeaders(json = false) {
    const token = localStorage.getItem('access_token');
    return {
        ...(json ? {'Content-Type': 'application/json'} : {}),
        ...(token ? {Authorization: `Bearer ${token}`} : {}),
    };
}

/**
 * Chronological notes feed for a KB entity (oldest→newest).
 *
 * @param {string} entityId
 * @param {number} [refetchKey=0]
 */
export function useEntityNotes(entityId, refetchKey = 0) {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(!!entityId);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!entityId) {
            setNotes([]);
            setLoading(false);
            setError(null);
            return undefined;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);

        fetch(`${BASE}/api/entities/${encodeURIComponent(entityId)}/notes`, {
            headers: authHeaders(),
        })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`${r.status}`))))
            .then((data) => {
                if (cancelled) return;
                setNotes(Array.isArray(data) ? data : []);
                setLoading(false);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error(`${LOG_PREFIX} Error:`, err);
                setError(err);
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [entityId, refetchKey]);

    return {notes, loading, error};
}

function requireAuthToken() {
    if (!localStorage.getItem('access_token')) {
        const err = new Error('Sign in to manage notes.');
        err.status = 401;
        return err;
    }
    return null;
}

function noteMutationError(response, fallback) {
    const err = new Error(
        response.status === 401
            ? 'Sign in to manage notes.'
            : response.status === 403
                ? 'You can only edit or delete your own notes.'
                : fallback,
    );
    err.status = response.status;
    return err;
}

/**
 * Create / update / delete notes on a KB entity. Requires a signed-in user (JWT).
 *
 * @param {{entityId: string, onMutationSuccess?: function}} opts
 */
export function useEntityNoteMutations({entityId, onMutationSuccess}) {
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        setError(null);
    }, [entityId]);

    const clearError = useCallback(() => setError(null), []);

    const postNote = useCallback(async (body) => {
        const trimmed = typeof body === 'string' ? body.trim() : '';
        if (!entityId || !trimmed) {
            const err = new Error('Note text is required.');
            setError(err);
            throw err;
        }
        const authErr = requireAuthToken();
        if (authErr) {
            setError(authErr);
            throw authErr;
        }

        setSaving(true);
        setError(null);
        try {
            const response = await fetch(
                `${BASE}/api/entities/${encodeURIComponent(entityId)}/notes`,
                {
                    method: 'POST',
                    headers: authHeaders(true),
                    body: JSON.stringify({body: trimmed}),
                },
            );
            if (!response.ok) {
                const err = noteMutationError(response, 'Failed to post note.');
                setError(err);
                throw err;
            }
            const data = await response.json();
            onMutationSuccess?.(data);
            return data;
        } catch (err) {
            console.error(`${LOG_PREFIX} Post error:`, err);
            if (!err.status) setError(err);
            throw err;
        } finally {
            setSaving(false);
        }
    }, [entityId, onMutationSuccess]);

    const updateNote = useCallback(async (noteId, body) => {
        const trimmed = typeof body === 'string' ? body.trim() : '';
        if (!entityId || !noteId || !trimmed) {
            const err = new Error('Note text is required.');
            setError(err);
            throw err;
        }
        const authErr = requireAuthToken();
        if (authErr) {
            setError(authErr);
            throw authErr;
        }

        setSaving(true);
        setError(null);
        try {
            const response = await fetch(
                `${BASE}/api/entities/${encodeURIComponent(entityId)}/notes/${encodeURIComponent(noteId)}`,
                {
                    method: 'PATCH',
                    headers: authHeaders(true),
                    body: JSON.stringify({body: trimmed}),
                },
            );
            if (!response.ok) {
                const err = noteMutationError(response, 'Failed to update note.');
                setError(err);
                throw err;
            }
            const data = await response.json();
            onMutationSuccess?.(data);
            return data;
        } catch (err) {
            console.error(`${LOG_PREFIX} Update error:`, err);
            if (!err.status) setError(err);
            throw err;
        } finally {
            setSaving(false);
        }
    }, [entityId, onMutationSuccess]);

    const deleteNote = useCallback(async (noteId) => {
        if (!entityId || !noteId) {
            const err = new Error('Note id is required.');
            setError(err);
            throw err;
        }
        const authErr = requireAuthToken();
        if (authErr) {
            setError(authErr);
            throw authErr;
        }

        setSaving(true);
        setError(null);
        try {
            const response = await fetch(
                `${BASE}/api/entities/${encodeURIComponent(entityId)}/notes/${encodeURIComponent(noteId)}`,
                {
                    method: 'DELETE',
                    headers: authHeaders(),
                },
            );
            if (!response.ok) {
                const err = noteMutationError(response, 'Failed to delete note.');
                setError(err);
                throw err;
            }
            onMutationSuccess?.({id: noteId, deleted: true});
            return true;
        } catch (err) {
            console.error(`${LOG_PREFIX} Delete error:`, err);
            if (!err.status) setError(err);
            throw err;
        } finally {
            setSaving(false);
        }
    }, [entityId, onMutationSuccess]);

    return {postNote, updateNote, deleteNote, saving, error, clearError};
}
